import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    MenuItem,
    Card,
    CardContent,
    CardActions,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InstructionChecker, { INSTRUCTION_TEMPLATES } from '../common/InstructionChecker';
import PromptEngineerFlow from '../instructions/promptEngineer/PromptEngineerFlow';
import { getInstructionLifecycleChipProps } from '../instructions/instructionLifecycleChip';
import SystemAlert from '../../common/SystemAlert.js';
import axios from 'axios';
import {
    materializePromptEngineerGenerationSession,
    publishPromptEngineerDraft
} from '../../services/promptEngineerService';

/**
 * AgentInstructions - shared component for agent instruction CRUD and selection
 * Props:
 *   agentId: string (optional)
 *   selectedInstructionId: string (optional)
 *   setSelectedInstructionId: fn (optional)
 *   onInstructionChange: fn (optional)
 *   onClose: fn (optional)
 *   showActions: bool (optional)
 */
const AgentInstructions = ({
    agentId,
    selectedInstructionId,
    setSelectedInstructionId,
    onInstructionChange,
    onClose,
    onSaveOrCreate,
    showActions = true,
    hideSelect = false,
    isEmbedded = false,
    showPromptEngineerHeaderAction = true,
    onCreateFlowActiveChange,
    onCreateFlowCloseHandlerChange
}) => {
    const [instructions, setInstructions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [alert, setAlert] = useState(null);
    const [createView, setCreateView] = useState(null); // null | chooser | template-list | template-edit | paste | promptEngineer
    const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [templateContent, setTemplateContent] = useState('');
    const [expandedTemplateKey, setExpandedTemplateKey] = useState(null);
    const [manualName, setManualName] = useState('');
    const [manualContent, setManualContent] = useState('');
    const [editMode, setEditMode] = useState('standard');
    const [promptEngineerHasUnsaved, setPromptEngineerHasUnsaved] = useState(false);
    const [confirmReopenPromptEngineerOpen, setConfirmReopenPromptEngineerOpen] = useState(false);
    const [confirmLeavePromptEngineerOpen, setConfirmLeavePromptEngineerOpen] = useState(false);
    const [pendingPromptEngineerDiscardAction, setPendingPromptEngineerDiscardAction] = useState(null);
    const [promptEngineerSaveForLaterAction, setPromptEngineerSaveForLaterAction] = useState(null);
    const [promptEngineerSessionInstruction, setPromptEngineerSessionInstruction] = useState(null);
    const [promptEngineerResumeSnapshot, setPromptEngineerResumeSnapshot] = useState(null);
    const previousSelectedInstructionIdRef = useRef(null);
    const handlePromptEngineerSaveForLaterRequestChange = React.useCallback((action) => {
        setPromptEngineerSaveForLaterAction(() => action || null);
    }, []);

    const buildPromptEngineerResumeSnapshot = React.useCallback((instruction) => {
        if (!instruction || instruction.sourceType !== 'ai_prompt_engineer') {
            return null;
        }

        const workspaceSnapshot = instruction?.promptEngineerMeta?.workspaceSnapshot;
        if (!workspaceSnapshot || typeof workspaceSnapshot !== 'object') {
            return null;
        }

        const stage = typeof workspaceSnapshot.stage === 'string' ? workspaceSnapshot.stage : 'interview';
        const currentPhase = Number.isInteger(workspaceSnapshot.currentPhase)
            ? workspaceSnapshot.currentPhase
            : 0;
        const selectedDraftVersion = instruction?.promptEngineerMeta?.selectedDraftVersion;

        return {
            stage,
            currentPhase,
            config: instruction?.promptEngineerConfig || null,
            instructionName: instruction?.name || '',
            draftText: typeof workspaceSnapshot.draftText === 'string'
                ? workspaceSnapshot.draftText
                : (instruction?.content || ''),
            optimizedText: typeof workspaceSnapshot.optimizedText === 'string'
                ? workspaceSnapshot.optimizedText
                : '',
            generationSessionKey: instruction?.promptEngineerMeta?.generationSessionKey || '',
            activeDraftVersion: selectedDraftVersion === 'optimized'
                ? 'optimized'
                : (workspaceSnapshot.activeDraftVersion === 'optimized' ? 'optimized' : 'original')
        };
    }, []);

    const isCreateFlowActive = createView !== null;
    const showInstructionEditor = !isCreateFlowActive && Boolean(selectedInstructionId && editing);
    const templateEntries = useMemo(() => Object.entries(INSTRUCTION_TEMPLATES), []);
    const isPromptEngineerInstruction = editing?.sourceType === 'ai_prompt_engineer';
    const canReopenPromptEngineer = isPromptEngineerInstruction && Boolean(editing?.promptEngineerConfig);
    const readyInstructions = useMemo(
        () => instructions.filter((instruction) => (instruction?.lifecycleState || 'READY').toUpperCase() === 'READY'),
        [instructions]
    );

    useEffect(() => {
        onCreateFlowActiveChange?.(isCreateFlowActive);
    }, [isCreateFlowActive, onCreateFlowActiveChange]);

    useEffect(() => {
        if (!onCreateFlowCloseHandlerChange) {
            return undefined;
        }

        onCreateFlowCloseHandlerChange(
            isCreateFlowActive
                ? () => exitCreateFlow({ restoreSelection: true })
                : null
        );

        return () => {
            onCreateFlowCloseHandlerChange(null);
        };
    }, [isCreateFlowActive, onCreateFlowCloseHandlerChange]);

    // Fetch instructions on mount.
    useEffect(() => {
        setLoading(true);
        axios.post('/instructions/getAll')
            .then(res => {
                setInstructions(res.data || []);
                setLoading(false);
                setError(null);
            })
            .catch(err => {
                setError('Failed to load instructions. Please try again later.');
                setInstructions([]);
                setLoading(false);
            });
    }, []);

    // If hideSelect is true, start in create mode.
    useEffect(() => {
        if (hideSelect) {
            if (previousSelectedInstructionIdRef.current == null && selectedInstructionId != null) {
                previousSelectedInstructionIdRef.current = selectedInstructionId;
            }
            setCreateView('chooser');
        }
    }, [hideSelect, selectedInstructionId]);

    // Keep editor in sync with selected instruction.
    useEffect(() => {
        if (isCreateFlowActive) return;
        if (!selectedInstructionId) {
            setEditing(null);
            return;
        }
        const found = instructions.find(i => String(i.id) === String(selectedInstructionId));
        setEditing(found ? { ...found } : null);
    }, [selectedInstructionId, instructions, isCreateFlowActive]);

    // Fallback hydration if parent selection is missing after modal reopen.
    useEffect(() => {
        if (!agentId || loading || isCreateFlowActive || selectedInstructionId) {
            return;
        }

        let cancelled = false;
        axios.post('/agents/getAgentDetail', null, { params: { agentId } })
            .then((response) => {
                if (cancelled) return;
                const instructionId = response?.data?.instructionId;
                if (!instructionId) return;
                if (setSelectedInstructionId) setSelectedInstructionId(instructionId);
                if (onInstructionChange) onInstructionChange(instructionId);
            })
            .catch(() => {
                // Non-blocking fallback: keep existing empty state if lookup fails.
            });

        return () => {
            cancelled = true;
        };
    }, [agentId, loading, isCreateFlowActive, selectedInstructionId, setSelectedInstructionId, onInstructionChange]);

    // Select handler
    const handleSelect = (id) => {
        setEditMode('standard');
        setPromptEngineerHasUnsaved(false);
        if (setSelectedInstructionId) setSelectedInstructionId(id);
        if (onInstructionChange) onInstructionChange(id);
    };

    // Field change handler
    const handleChange = (field) => (e) => {
        setEditing(prev => ({ ...prev, [field]: e.target.value }));
    };

    const formatTemplateTitle = (key) => {
        if (!key) return '';
        const withSpaces = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/[-_]/g, ' ')
            .trim();
        return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    };

    const resetTemplateSelection = () => {
        setSelectedTemplateKey(null);
        setTemplateName('');
        setTemplateContent('');
        setExpandedTemplateKey(null);
    };

    const exitCreateFlow = ({ restoreSelection = true } = {}) => {
        setCreateView(null);
        resetTemplateSelection();
        setManualName('');
        setManualContent('');

        if (restoreSelection && previousSelectedInstructionIdRef.current != null) {
            handleSelect(previousSelectedInstructionIdRef.current);
        }
    };

    const handleStartCreateFlow = () => {
        if (previousSelectedInstructionIdRef.current == null && selectedInstructionId != null) {
            previousSelectedInstructionIdRef.current = selectedInstructionId;
        }
        setCreateView('chooser');
    };

    const ensureUniqueName = (candidateName) => {
        const base = String(candidateName || '').trim();
        if (!base) return '';

        const existingNames = new Set(
            instructions
                .map((instr) => (instr.name || instr.instructionName || '').trim().toLowerCase())
                .filter(Boolean)
        );

        if (!existingNames.has(base.toLowerCase())) {
            return base;
        }

        let counter = 2;
        let candidate = `${base} (${counter})`;
        while (existingNames.has(candidate.toLowerCase())) {
            counter += 1;
            candidate = `${base} (${counter})`;
        }

        setAlert({ severity: 'info', message: `An instruction with that name exists - saving as "${candidate}".` });
        return candidate;
    };

    const resolveErrorMessage = (err, fallback) => {
        return err?.response?.data?.message
            || err?.response?.data?.error
            || err?.message
            || fallback;
    };

    const buildPromptEngineerMeta = (instructionPayload, overrides = {}) => ({
        ...(instructionPayload?.promptEngineerMeta || {}),
        ...overrides
    });

    const findExistingRevisionDraft = (sourceInstructionId) => {
        if (!sourceInstructionId) return null;
        return instructions.find((instruction) => {
            const lifecycleState = (instruction?.lifecycleState || '').toUpperCase();
            const meta = instruction?.promptEngineerMeta || {};
            return lifecycleState === 'DRAFT'
                && instruction?.sourceType === 'ai_prompt_engineer'
                && meta?.draftRole === 'revision_draft'
                && Number(meta?.sourceInstructionId) === Number(sourceInstructionId);
        }) || null;
    };

    const resolvePromptEngineerEditTarget = React.useCallback((sourceInstruction) => {
        if (!sourceInstruction) {
            return null;
        }

        const lifecycleState = (sourceInstruction?.lifecycleState || '').toUpperCase();
        if (lifecycleState === 'READY') {
            const existingRevisionDraft = findExistingRevisionDraft(sourceInstruction.id);
            if (existingRevisionDraft?.id) {
                return existingRevisionDraft;
            }
        }

        return sourceInstruction;
    }, [instructions]);

    const persistPromptEngineerDraft = async (instructionPayload, options = {}) => {
        const {
            instructionId = null,
            sourceInstructionId = null,
            draftRole = 'standalone_draft'
        } = options;

        const generationSessionKey = instructionPayload?.promptEngineerMeta?.generationSessionKey;
        if (generationSessionKey) {
            const materialized = await materializePromptEngineerGenerationSession(generationSessionKey, {
                instructionId,
                sourceInstructionId,
                saveIntent: 'draft_save',
                lifecycleState: 'DRAFT',
                draftRole
            });
            return materialized?.instruction || null;
        }

        const payload = {
            ...instructionPayload,
            id: instructionId || undefined,
            lifecycleState: 'DRAFT',
            lifecycleReason: 'Saved for later',
            promptEngineerMeta: buildPromptEngineerMeta(instructionPayload, {
                draftRole,
                sourceInstructionId: sourceInstructionId || undefined
            })
        };

        if (instructionId) {
            const result = await axios.post('/instructions/update', payload);
            return result?.data || null;
        }

        const { id, ...createPayload } = payload;
        const result = await axios.post('/instructions/create', createPayload);
        return result?.data || null;
    };

    const publishStandalonePromptEngineerInstruction = async (instructionPayload) => {
        const generationSessionKey = instructionPayload?.promptEngineerMeta?.generationSessionKey;
        if (generationSessionKey) {
            const materialized = await materializePromptEngineerGenerationSession(generationSessionKey, {
                saveIntent: 'publish',
                lifecycleState: 'READY',
                draftRole: 'standalone_draft'
            });
            return materialized?.instruction || null;
        }

        const { id, ...createPayload } = {
            ...instructionPayload,
            lifecycleState: 'READY',
            lifecycleReason: null,
            promptEngineerMeta: buildPromptEngineerMeta(instructionPayload, {
                draftRole: 'standalone_draft'
            })
        };
        const result = await axios.post('/instructions/create', createPayload);
        return result?.data || null;
    };

    const transitionInstructionToReady = async (instructionId) => {
        if (!instructionId) return;
        await axios.post('/instructions/transitionLifecycle', {
            id: instructionId,
            lifecycleState: 'READY',
            reason: 'Explicit readiness transition before agent attachment'
        });
    };

    const attachInstructionToAgent = async (instructionId) => {
        if (!agentId || !instructionId) return;
        await axios.post('/agents/updateInstruction', {
            agentId,
            instructionId
        });
    };

    const createInstruction = async (instructionPayload) => {
        const incomingName = instructionPayload?.name;
        const normalizedName = ensureUniqueName(incomingName);
        if (!normalizedName) {
            setAlert({ severity: 'error', message: 'Instruction name is required.' });
            return null;
        }

        setSaving(true);
        try {
            const payload = {
                ...instructionPayload,
                name: normalizedName
            };

            // Do not send local-only id in create request.
            delete payload.id;

            if (agentId) {
                payload.agentId = agentId;
            }

            const result = await axios.post('/instructions/create', payload);
            const created = result?.data;
            if (!created?.id) {
                throw new Error('Instruction created but no id returned.');
            }

            setInstructions((prev) => [...prev, created]);
            setEditing({ ...created });
            setAlert({ severity: 'success', message: 'Instruction created!' });

            handleSelect(created.id);

            await transitionInstructionToReady(created.id);
            await attachInstructionToAgent(created.id);

            if (onSaveOrCreate) {
                onSaveOrCreate(created.id);
            }

            previousSelectedInstructionIdRef.current = null;
            exitCreateFlow({ restoreSelection: false });
            return created;
        } catch (err) {
            const errorMsg = resolveErrorMessage(err, 'Create failed');
            setAlert({ severity: 'error', message: errorMsg });
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Save handler (update existing only).
    const handleSave = async () => {
        if (!editing?.id || !instructions.some(i => String(i.id) === String(editing.id))) {
            setAlert({ severity: 'warning', message: 'Select an instruction from the list or click New to create one before saving.' });
            return;
        }
        setSaving(true);
        try {
            const agentInstruction = agentId ? { ...editing, agentId } : { ...editing };

            console.log('[AgentInstructions] Updating instruction:', agentInstruction);
            const result = await axios.post('/instructions/update', agentInstruction);
            const updated = result?.data;
            if (!updated?.id) {
                throw new Error('Update failed: no instruction id returned.');
            }

            setInstructions(prev => prev.map(i => String(i.id) === String(updated.id) ? updated : i));
            setAlert({ severity: 'success', message: 'Instruction updated!' });

            setEditing(result.data);
            handleSelect(result.data.id);

            await transitionInstructionToReady(result.data.id);
            await attachInstructionToAgent(result.data.id);

            // Call onSaveOrCreate callback if provided
            if (onSaveOrCreate) {
                onSaveOrCreate(result.data.id);
            }
        } catch (err) {
            // Try to show a more descriptive error if available
            const errorMsg = resolveErrorMessage(err, 'Save failed');
            setAlert({ severity: 'error', message: errorMsg });
        }
        setSaving(false);
    };

    const handleSavePromptEngineerEdit = async (instructionPayload) => {
        const activePromptEngineerInstruction = promptEngineerSessionInstruction || editing;

        if (!activePromptEngineerInstruction?.id) {
            throw new Error('No instruction selected for AI Architect update.');
        }

        setSaving(true);
        try {
            const activeLifecycleState = (activePromptEngineerInstruction?.lifecycleState || '').toUpperCase();

            if (activeLifecycleState === 'DRAFT') {
                const sourceInstructionId = activePromptEngineerInstruction?.promptEngineerMeta?.sourceInstructionId || editing?.id || null;
                const generationSessionKey = instructionPayload?.promptEngineerMeta?.generationSessionKey;

                let draftInstructionId = activePromptEngineerInstruction.id;
                if (generationSessionKey) {
                    const materializedDraft = await materializePromptEngineerGenerationSession(generationSessionKey, {
                        instructionId: activePromptEngineerInstruction.id,
                        sourceInstructionId,
                        saveIntent: 'draft_save',
                        lifecycleState: 'DRAFT',
                        draftRole: sourceInstructionId ? 'revision_draft' : 'standalone_draft'
                    });
                    draftInstructionId = materializedDraft?.instruction?.id || activePromptEngineerInstruction.id;
                } else {
                    const persistedDraft = await axios.post('/instructions/update', {
                        ...activePromptEngineerInstruction,
                        ...instructionPayload,
                        id: activePromptEngineerInstruction.id,
                        lifecycleState: 'DRAFT',
                        lifecycleReason: 'Saved for later'
                    });
                    draftInstructionId = persistedDraft?.data?.id || activePromptEngineerInstruction.id;
                }

                const published = await publishPromptEngineerDraft(draftInstructionId);
                const publishedInstruction = published?.instruction || null;
                if (!publishedInstruction?.id) {
                    throw new Error('Instruction published but no id returned.');
                }

                setInstructions((prev) => prev.map((instruction) => (
                    String(instruction.id) === String(publishedInstruction.id) ? publishedInstruction : instruction
                )));
                setEditing(publishedInstruction);
                handleSelect(publishedInstruction.id);
                setPromptEngineerHasUnsaved(false);

                if (published.cleanupRequired) {
                    setAlert({ severity: 'warning', message: published.cleanupWarning || 'Instruction published successfully. Draft cleanup is still pending.' });
                } else {
                    setAlert({ severity: 'success', message: 'Instruction published successfully!' });
                }

                exitPromptEngineerEditMode();

                if (onSaveOrCreate) {
                    onSaveOrCreate(publishedInstruction.id);
                }

                return publishedInstruction;
            }

            const result = await axios.post('/instructions/update', {
                ...activePromptEngineerInstruction,
                ...instructionPayload,
                id: activePromptEngineerInstruction.id
            });

            const updated = result?.data;
            if (!updated?.id) {
                throw new Error('Update failed: no instruction id returned.');
            }

            await transitionInstructionToReady(updated.id);
            await attachInstructionToAgent(updated.id);

            setInstructions(prev => prev.map(i => String(i.id) === String(updated.id) ? updated : i));
            setEditing(updated);
            setEditMode('standard');
            setPromptEngineerHasUnsaved(false);
            setAlert({ severity: 'success', message: 'Instruction updated!' });

            if (onSaveOrCreate) {
                onSaveOrCreate(updated.id);
            }

            return updated;
        } catch (err) {
            const errorMsg = resolveErrorMessage(err, 'Save failed');
            setAlert({ severity: 'error', message: errorMsg });
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleSavePromptEngineerDraftFromEdit = async (instructionPayload) => {
        if (!editing?.id) {
            throw new Error('No instruction selected for AI Architect draft save.');
        }

        setSaving(true);
        try {
            const existingRevisionDraft = findExistingRevisionDraft(editing.id);
            const savedDraft = await persistPromptEngineerDraft(instructionPayload, {
                instructionId: existingRevisionDraft?.id || null,
                sourceInstructionId: editing.id,
                draftRole: 'revision_draft'
            });

            if (!savedDraft?.id) {
                throw new Error('Draft saved but no instruction id returned.');
            }

            setInstructions((prev) => {
                const withoutExisting = prev.filter((instruction) => String(instruction.id) !== String(savedDraft.id));
                return [...withoutExisting, savedDraft];
            });
            setEditMode('standard');
            setPromptEngineerHasUnsaved(false);
            setAlert({
                severity: 'success',
                message: 'Draft saved for later. Your agent will keep using the current published instructions until you publish this revision.'
            });
            return savedDraft;
        } catch (err) {
            const errorMsg = resolveErrorMessage(err, 'Failed to save draft');
            setAlert({ severity: 'error', message: errorMsg });
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const handlePublishStandalonePromptEngineer = async (instructionPayload) => {
        setSaving(true);
        try {
            const publishedInstruction = await publishStandalonePromptEngineerInstruction(instructionPayload);
            if (!publishedInstruction?.id) {
                throw new Error('Instruction published but no id returned.');
            }

            setInstructions((prev) => {
                const withoutExisting = prev.filter((instruction) => String(instruction.id) !== String(publishedInstruction.id));
                return [...withoutExisting, publishedInstruction];
            });
            setAlert({
                severity: 'success',
                message: 'Instruction published. It is now ready to attach, but this agent will keep using its currently selected instructions until you choose the new one.'
            });
            exitCreateFlow({ restoreSelection: true });
            return publishedInstruction;
        } catch (err) {
            const errorMsg = resolveErrorMessage(err, 'Publish failed');
            setAlert({ severity: 'error', message: errorMsg });
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const handleSavePromptEngineerDraftFromCreate = async (instructionPayload) => {
        setSaving(true);
        try {
            const savedDraft = await persistPromptEngineerDraft(instructionPayload, {
                draftRole: 'standalone_draft'
            });
            if (!savedDraft?.id) {
                throw new Error('Draft saved but no instruction id returned.');
            }

            setInstructions((prev) => {
                const withoutExisting = prev.filter((instruction) => String(instruction.id) !== String(savedDraft.id));
                return [...withoutExisting, savedDraft];
            });
            setAlert({
                severity: 'success',
                message: 'Your draft will be saved for later. This agent will continue using its currently selected instructions.'
            });
            exitCreateFlow({ restoreSelection: true });
            return savedDraft;
        } catch (err) {
            const errorMsg = resolveErrorMessage(err, 'Failed to save draft');
            setAlert({ severity: 'error', message: errorMsg });
            throw err;
        } finally {
            setSaving(false);
        }
    };

    const requestLeavePromptEngineer = (leaveAction) => {
        if (!promptEngineerHasUnsaved) {
            leaveAction?.();
            return;
        }
        setPendingPromptEngineerDiscardAction(() => leaveAction || null);
        setConfirmLeavePromptEngineerOpen(true);
    };

    const enterPromptEngineerEditMode = React.useCallback(() => {
        const sourceInstruction = editing;
        const targetInstruction = resolvePromptEngineerEditTarget(sourceInstruction);

        setPromptEngineerSessionInstruction(targetInstruction || sourceInstruction || null);
        setPromptEngineerResumeSnapshot(buildPromptEngineerResumeSnapshot(targetInstruction || sourceInstruction || null));
        setEditMode('promptEngineer');
    }, [editing, resolvePromptEngineerEditTarget, buildPromptEngineerResumeSnapshot]);

    const promptEngineerReopenTargetInstruction = promptEngineerSessionInstruction || editing;
    const isReopeningExistingRevisionDraft = Boolean(
        promptEngineerReopenTargetInstruction
        && editing
        && String(promptEngineerReopenTargetInstruction.id) !== String(editing.id)
    );
    const promptEngineerLifecycleInstruction = editMode === 'promptEngineer' && promptEngineerSessionInstruction
        ? promptEngineerSessionInstruction
        : editing;

    const exitPromptEngineerEditMode = React.useCallback(() => {
        setPromptEngineerSessionInstruction(null);
        setPromptEngineerResumeSnapshot(null);
        setEditMode('standard');
    }, []);

    const handleCancel = () => {
        if (editMode === 'promptEngineer') {
            requestLeavePromptEngineer(() => {
                onClose?.();
            });
            return;
        }
        onClose?.();
    };

    // UI
    if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

    const renderCreationChooser = () => (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">How would you like to create your instructions?</Typography>
                <Button
                    startIcon={<ArrowBackIcon />}
                    variant="text"
                    onClick={() => exitCreateFlow({ restoreSelection: true })}
                    disabled={saving}
                >
                    Back
                </Button>
            </Stack>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 2,
                    alignItems: 'stretch'
                }}
            >
                <Card elevation={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <SmartToyIcon color="primary" />
                            <Typography variant="h6">Meet Your AI Instruction Architect</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: { xs: 'auto', md: '3em' }, lineHeight: 1.5 }}>
                            Answer a few business questions and the AI Instruction Architect will engineer production-ready instructions for you.
                        </Typography>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                        <Button variant="contained" onClick={() => setCreateView('promptEngineer')}>Start Your Interview</Button>
                    </CardActions>
                </Card>

                {/*
                <Card elevation={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <AutoAwesomeIcon color="primary" />
                            <Typography variant="h6">Use existing templates</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: { xs: 'auto', md: '3em' }, lineHeight: 1.5 }}>
                            Browse curated starter instructions and customize them to fit your needs.
                        </Typography>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                        <Button variant="contained" onClick={() => setCreateView('template-list')}>Browse Templates</Button>
                    </CardActions>
                </Card>
                */}

                <Card elevation={3} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <ContentPasteGoIcon color="primary" />
                            <Typography variant="h6">I already have instructions</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: { xs: 'auto', md: '3em' }, lineHeight: 1.5 }}>
                            Paste your existing instructions or write them in your own words.
                        </Typography>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                        <Button variant="contained" onClick={() => setCreateView('paste')}>Paste Instructions</Button>
                    </CardActions>
                </Card>
            </Box>
        </Box>
    );

    const renderTemplateList = () => (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Select a template to start from</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => setCreateView('chooser')} variant="text" disabled={saving}>
                    Back
                </Button>
            </Stack>
            <Stack spacing={1}>
                {templateEntries.map(([key, content]) => (
                    <Accordion
                        elevation={1}
                        key={key}
                        expanded={expandedTemplateKey === key}
                        onChange={() => setExpandedTemplateKey(expandedTemplateKey === key ? null : key)}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 600 }}>{formatTemplateTitle(key)}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Stack spacing={2}>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                                    {content}
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        setSelectedTemplateKey(key);
                                        setTemplateName(formatTemplateTitle(key));
                                        setTemplateContent(INSTRUCTION_TEMPLATES[key] || '');
                                        setCreateView('template-edit');
                                    }}
                                >
                                    Use Template
                                </Button>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Stack>
        </Box>
    );

    const renderTemplateEditor = () => (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Customize Template</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => setCreateView('template-list')} variant="text" disabled={saving}>
                    Back to templates
                </Button>
            </Stack>
            <Stack spacing={2}>
                <TextField
                    label="Instruction Name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    fullWidth
                    disabled={saving}
                />
                <TextField
                    label="Instruction Content"
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    minRows={12}
                    multiline
                    fullWidth
                    disabled={saving}
                />
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="contained"
                        onClick={() => createInstruction({ name: templateName.trim(), content: templateContent })}
                        disabled={saving || !templateName.trim() || !templateContent.trim()}
                    >
                        Save Instruction
                    </Button>
                    <Button variant="outlined" onClick={() => exitCreateFlow({ restoreSelection: true })} disabled={saving}>
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );

    const renderPasteEditor = () => (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Paste instructions</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => setCreateView('chooser')} variant="text" disabled={saving}>
                    Back
                </Button>
            </Stack>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Stack spacing={2}>
                        <TextField
                            label="Instruction Name"
                            value={manualName}
                            onChange={(e) => setManualName(e.target.value)}
                            fullWidth
                            disabled={saving}
                        />
                        <TextField
                            label="Instruction Content"
                            value={manualContent}
                            onChange={(e) => setManualContent(e.target.value)}
                            minRows={14}
                            multiline
                            fullWidth
                            placeholder="Paste your instructions here..."
                            disabled={saving}
                        />
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                onClick={() => createInstruction({ name: manualName.trim(), content: manualContent })}
                                disabled={saving || !manualName.trim() || !manualContent.trim()}
                            >
                                Save Instruction
                            </Button>
                            <Button variant="outlined" onClick={() => exitCreateFlow({ restoreSelection: true })} disabled={saving}>
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        width: { xs: '100%', lg: 360 },
                        flexShrink: 0,
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        p: 2
                    }}
                >
                    <InstructionChecker
                        content={manualContent}
                        onTemplateInsert={(template) => {
                            setManualContent(template);
                            if (!manualName?.trim()) {
                                setManualName('Imported Instruction');
                            }
                        }}
                        width="100%"
                    />
                </Box>
            </Box>
        </Box>
    );

    const renderCreateContent = () => {
        switch (createView) {
            case 'template-list':
                return renderTemplateList();
            case 'template-edit':
                return renderTemplateEditor();
            case 'paste':
                return renderPasteEditor();
            case 'promptEngineer':
                return (
                    <PromptEngineerFlow
                        onExit={() => exitCreateFlow({ restoreSelection: true })}
                        onSaveInstruction={handlePublishStandalonePromptEngineer}
                        onSaveForLater={handleSavePromptEngineerDraftFromCreate}
                        isSaving={saving}
                        flowContext="agent_new_standalone"
                        onSaveForLaterRequestChange={handlePromptEngineerSaveForLaterRequestChange}
                    />
                );
            case 'chooser':
            default:
                return renderCreationChooser();
        }
    };

    if (isCreateFlowActive) {
        return (
            <Box sx={{ width: '100%' }}>
                {renderCreateContent()}
                {alert && (
                    <SystemAlert open={!!alert} severity={alert.severity} message={alert.message} onClose={() => setAlert(null)} />
                )}
            </Box>
        );
    }

    // ...existing code...
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'flex-start', width: '100%', textAlign: 'left' }}>
            {/* Left: Edit form and controls */}
            <Box sx={{ flex: 1 }}>
                {/* Top Text under Agent Instructions title */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    You can use an instructions template to define how your agent should behave and what tasks it should perform. Feel free to edit the template to customize your agent. If you write your instructions from scratch, please ensure they're short yet comprehensive. Your instructions will guide your agent's responses.
                </Typography>
                {!hideSelect && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <TextField
                            select
                            label="Select Instruction"
                            value={selectedInstructionId != null && selectedInstructionId !== '' ? String(selectedInstructionId) : ''}
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (v) => {
                                    let text = '';
                                    if (v === '' || v == null) text = 'Select an instruction...';
                                    else {
                                        const i = readyInstructions.find(x => String(x.id) === String(v));
                                        text = i?.name || i?.instructionName || `Instruction ${i?.id}` || '';
                                    }
                                    return (
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                width: '100%',
                                                textAlign: 'left',
                                                color: (v === '' || v == null) ? 'text.secondary' : 'text.primary'
                                            }}
                                        >
                                            {text}
                                        </Box>
                                    );
                                },
                                sx: {
                                    '& .MuiSelect-select': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        minHeight: '1.4375em'
                                    }
                                }
                            }}
                            onChange={e => {
                                const id = e.target.value;
                                if (!id) {
                                    return;
                                }
                                const found = readyInstructions.find(i => String(i.id) === String(id));
                                if (found) {
                                    setEditing({ ...found });
                                    if (setSelectedInstructionId) setSelectedInstructionId(id);
                                    if (onInstructionChange) onInstructionChange(id);
                                }
                            }}
                            fullWidth
                            sx={{
                                flex: '1 1 220px',
                                minWidth: { xs: '100%', sm: 220 },
                                maxWidth: { sm: '100%' },
                                background: '#fff',
                                mt: 2,
                                textAlign: 'left'
                            }}
                            InputProps={{ style: { textAlign: 'left' } }}
                        >
                            {readyInstructions.length > 0 && (
                                <MenuItem value="" disabled>Select an instruction...</MenuItem>
                            )}
                            {readyInstructions.length === 0 && (
                                <MenuItem value="" disabled>No instructions available</MenuItem>
                            )}
                            {readyInstructions.map(instr => (
                                <MenuItem key={instr.id} value={String(instr.id)}>{instr.name || instr.instructionName || `Instruction ${instr.id}`}</MenuItem>
                            ))}
                        </TextField>
                        <Button startIcon={<AddIcon />} onClick={handleStartCreateFlow} sx={{ minWidth: 80, mt: 2, flexShrink: 0 }}>New</Button>
                    </Box>
                )}
                {!hideSelect && !showInstructionEditor && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Select a READY instruction above, or click New to write instructions from scratch. Draft instructions can be continued from the Instructions page and cannot be attached to an agent.
                    </Typography>
                )}
                {showInstructionEditor && (
                    <>
                        {showPromptEngineerHeaderAction && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', mb: 1 }}>
                                {isPromptEngineerInstruction && (
                                    <Chip label="Engineered By AI Instruction Architect" color="primary" size="small" />
                                )}
                                {promptEngineerLifecycleInstruction?.lifecycleState && (
                                    <Chip
                                        {...getInstructionLifecycleChipProps(promptEngineerLifecycleInstruction.lifecycleState)}
                                        size="small"
                                    />
                                )}
                                {canReopenPromptEngineer && editMode === 'standard' && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<SmartToyIcon />}
                                        onClick={() => {
                                            const targetInstruction = resolvePromptEngineerEditTarget(editing);
                                            setPromptEngineerSessionInstruction(targetInstruction || editing || null);
                                            setPromptEngineerResumeSnapshot(buildPromptEngineerResumeSnapshot(targetInstruction || editing || null));
                                            setConfirmReopenPromptEngineerOpen(true);
                                        }}
                                    >
                                        Edit Interview Answers
                                    </Button>
                                )}
                                {editMode === 'promptEngineer' && canReopenPromptEngineer && (
                                    <Button
                                        variant="text"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => requestLeavePromptEngineer(() => exitPromptEngineerEditMode())}
                                    >
                                        Back to standard editor
                                    </Button>
                                )}
                            </Stack>
                        )}

                        {editMode === 'promptEngineer' && canReopenPromptEngineer && (
                            <PromptEngineerFlow
                                onExit={() => requestLeavePromptEngineer(() => exitPromptEngineerEditMode())}
                                onSaveInstruction={handleSavePromptEngineerEdit}
                                onSaveForLater={handleSavePromptEngineerDraftFromEdit}
                                isSaving={saving}
                                initialConfig={(promptEngineerSessionInstruction || editing)?.promptEngineerConfig || null}
                                initialInstructionName={(promptEngineerSessionInstruction || editing)?.name || ''}
                                initialDraftText={(promptEngineerSessionInstruction || editing)?.content || ''}
                                resumeSnapshot={promptEngineerResumeSnapshot}
                                existingInstructionId={(promptEngineerSessionInstruction || editing)?.id || null}
                                reopenMode={true}
                                flowContext="agent_edit_revision"
                                onUnsavedChange={setPromptEngineerHasUnsaved}
                                onSaveForLaterRequestChange={handlePromptEngineerSaveForLaterRequestChange}
                            />
                        )}

                        {editMode === 'standard' && (
                            <>
                                {/* Edit fields */}
                                <TextField
                                    label="Instruction Name"
                                    value={editing?.name || ''}
                                    onChange={handleChange('name')}
                                    margin="normal"
                                    sx={{ mb: 1, width: '100%' }}
                                    required
                                    disabled={saving}
                                />
                                <Box sx={{ width: '100%', mb: 2, maxHeight: 300, overflow: 'auto' }}>
                                    <TextField
                                        label="Instruction Content"
                                        variant="outlined"
                                        value={editing?.content || ''}
                                        onChange={handleChange('content')}
                                        margin="normal"
                                        fullWidth
                                        multiline
                                        minRows={6}
                                        sx={{ width: '100%', mb: 0 }}
                                        required
                                        disabled={saving}
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Provide clear instructions about how the agent should behave, what tone to use, and any specific guidelines it should follow.
                                </Typography>
                            </>
                        )}
                    </>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                    {showActions && (
                        <>
                            {showInstructionEditor && editMode === 'standard' && (
                                <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !editing?.name || !editing?.content}
                                    startIcon={<SaveIcon />}
                                >
                                    {isEmbedded && 'Save Instruction'}
                                    {!isEmbedded && 'Save'}
                                </Button>
                            )}
                            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
                {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
            </Box>
            {/* Right: Instruction Checker */}
            {showInstructionEditor && editMode === 'standard' && (
                <Box sx={{ flex: 1, alignSelf: 'flex-start', mt: 0 }}>
                    <InstructionChecker
                        content={editing?.content || ''}
                        onTemplateInsert={template => setEditing(editing => ({ ...editing, content: template }))}
                        instructionId={editing?.id || null}
                        onContentRestore={(historyContent) => {
                            setEditing(prev => ({ ...prev, content: historyContent }));
                            setAlert({
                                severity: 'info',
                                message: 'History content loaded into the editor. Click Save to persist your changes.'
                            });
                        }}
                    />
                </Box>
            )}
            {/* System Alert for feedback */}
            {alert && (
                <SystemAlert open={!!alert} severity={alert.severity} message={alert.message} onClose={() => setAlert(null)} />
            )}

            <Dialog
                open={confirmReopenPromptEngineerOpen}
                onClose={() => setConfirmReopenPromptEngineerOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {isReopeningExistingRevisionDraft ? 'Continue Your Draft?' : 'Reopen AI Instruction Architect?'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {isReopeningExistingRevisionDraft
                            ? 'You have a saved draft of these instructions. Continue where you left off to finish your changes. Your agent will continue using the current published instructions until you publish this draft.'
                            : 'Reopening the AI Instruction Architect uses your original interview responses. Manual changes made directly to the instructions will not be preserved unless you modify the interview answers accordingly.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmReopenPromptEngineerOpen(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setConfirmReopenPromptEngineerOpen(false);
                            enterPromptEngineerEditMode();
                        }}
                        variant="contained"
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmLeavePromptEngineerOpen}
                onClose={() => {
                    setConfirmLeavePromptEngineerOpen(false);
                    setPendingPromptEngineerDiscardAction(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Discard AI Instruction Architect Session?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Your interview responses have not been saved. Leaving now will permanently discard your progress. This cannot be recovered.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={async () => {
                            try {
                                await promptEngineerSaveForLaterAction?.({ leaveAfterSave: false });
                                const continueAction = pendingPromptEngineerDiscardAction;
                                setConfirmLeavePromptEngineerOpen(false);
                                setPendingPromptEngineerDiscardAction(null);
                                continueAction?.();
                            } catch (err) {
                                // Preserve current flow on save failure.
                            }
                        }}
                        variant="contained"
                        disabled={!promptEngineerSaveForLaterAction || saving}
                    >
                        Save and Leave
                    </Button>
                    <Button
                        onClick={() => {
                            setConfirmLeavePromptEngineerOpen(false);
                            setPendingPromptEngineerDiscardAction(null);
                        }}
                        variant="outlined"
                    >
                        Continue Editing
                    </Button>
                    <Button
                        onClick={() => {
                            const continueAction = pendingPromptEngineerDiscardAction;
                            setConfirmLeavePromptEngineerOpen(false);
                            setPendingPromptEngineerDiscardAction(null);
                            setPromptEngineerHasUnsaved(false);
                            continueAction?.();
                        }}
                        color="error"
                        variant="contained"
                    >
                        Leave Without Saving
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AgentInstructions;
